import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Download, Upload, CheckCircle2, XCircle, FileSpreadsheet,
  ArrowLeft, AlertTriangle, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  buildBulkTemplate, parseBulkFile, validateRowsClient, downloadFailedRows,
} from "@/lib/bulkVouchers";

const VTYPE_BADGE = {
  Journal: "bg-purple-50 text-purple-700",
  Payment: "bg-orange-50 text-orange-700",
  Receipt: "bg-emerald-50 text-emerald-700",
  Contra: "bg-blue-50 text-blue-700",
};

function Inner() {
  const { active, activeId } = useCompany();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [ledgers, setLedgers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // rows[]  with .issues[]
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    if (!activeId) return;
    api.get(`/companies/${activeId}/ledgers`).then((r) => setLedgers(r.data));
  }, [activeId]);

  const ledgerNames = useMemo(() => ledgers.map((l) => l.name), [ledgers]);

  const validRows = useMemo(
    () => (preview || []).filter((r) => r.issues.length === 0),
    [preview],
  );
  const invalidRows = useMemo(
    () => (preview || []).filter((r) => r.issues.length > 0),
    [preview],
  );

  const onCreate = async () => {
    if (!active) return;
    if (!ledgers.length) {
      toast.error("Create at least one ledger before generating the template.");
      return;
    }
    setBusy(true);
    try {
      await buildBulkTemplate(ledgerNames, active.name);
      toast.success("Template downloaded — open it in Excel and fill rows.");
    } catch (err) {
      toast.error(`Failed to generate template: ${err?.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  const onPick = () => fileRef.current?.click();

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setImportResult(null);
    try {
      const parsed = await parseBulkFile(file);
      if (!parsed.length) {
        toast.error("No data rows found in the uploaded file.");
        setPreview(null);
        return;
      }
      const validated = validateRowsClient(parsed, ledgerNames);
      setPreview(validated);
      const okCount = validated.filter((r) => r.issues.length === 0).length;
      toast.success(`${parsed.length} rows parsed — ${okCount} look good, ${parsed.length - okCount} need attention`);
    } catch (err) {
      toast.error(err.message || "Could not parse file");
      setPreview(null);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onImport = async () => {
    if (!validRows.length) return;
    setBusy(true);
    try {
      const payload = validRows.map(({ issues, excel_row, ...row }) => row);
      const { data } = await api.post(`/companies/${activeId}/vouchers/bulk`, { rows: payload });

      // Map server results back to original Excel row numbers
      const decorated = data.results.map((r, idx) => ({
        ...r,
        row: validRows[idx]?.excel_row || r.row,
        ...validRows[idx],
      }));
      setImportResult({ ...data, results: decorated });
      setPreview(null);

      if (data.created > 0) toast.success(`${data.created} voucher${data.created === 1 ? "" : "s"} created`);
      if (data.failed > 0) toast.error(`${data.failed} server-side failure${data.failed === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const onDownloadFailed = async () => {
    const failed = importResult?.results.filter((r) => !r.ok).map((r) => ({
      row: r.row, date: r.date, debit_ledger: r.debit_ledger, credit_ledger: r.credit_ledger,
      narration: r.narration, amount: r.amount, voucher_type: r.voucher_type, error: r.error,
    })) || [];
    if (!failed.length) return;
    await downloadFailedRows(failed, ledgerNames, active?.name);
  };

  return (
    <>
      <PageHeader
        title="Upload Bulk Entries"
        subtitle="Create many vouchers in one go from an Excel sheet"
        actions={
          <Button variant="outline" className="h-8" onClick={() => navigate("/vouchers")} data-testid="btn-back-vouchers">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Vouchers
          </Button>
        }
      />
      <div className="p-6 space-y-4 max-w-6xl">
        {/* Step 1: Template + Upload cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <section className="border border-gray-200 rounded bg-white p-5">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded bg-blue-50 text-blue-700 flex items-center justify-center">
                <Download className="w-4 h-4" />
              </div>
              <h2 className="font-semibold text-gray-900">Create new excel file</h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Downloads a pre-formatted template with dropdowns (ledger lists + voucher type), date / amount validation,
              and 2 sample rows. <strong>{ledgers.length}</strong> ledger{ledgers.length === 1 ? "" : "s"} from
              <span className="font-medium"> {active?.name}</span> will be in the dropdowns.
            </p>
            <Button onClick={onCreate} disabled={busy || !ledgers.length} className="bg-blue-700 hover:bg-blue-800 h-9" data-testid="btn-create-template">
              <FileSpreadsheet className="w-4 h-4 mr-1.5" />
              {busy ? "Generating…" : "Create new excel file"}
            </Button>
            {!ledgers.length && (
              <p className="text-xs text-red-700 mt-2">No ledgers found — create at least one ledger first.</p>
            )}
          </section>

          <section className="border border-gray-200 rounded bg-white p-5">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded bg-emerald-50 text-emerald-700 flex items-center justify-center">
                <Upload className="w-4 h-4" />
              </div>
              <h2 className="font-semibold text-gray-900">Upload excel file</h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Upload the filled template. We&apos;ll show a <strong>preview</strong> with any issues before importing —
              so you can verify before vouchers are actually created.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={onUpload}
              className="hidden"
              data-testid="input-upload-bulk"
            />
            <Button onClick={onPick} disabled={busy} variant="outline" className="h-9" data-testid="btn-upload-bulk">
              <Upload className="w-4 h-4 mr-1.5" />
              {busy ? "Processing…" : "Upload excel file"}
            </Button>
          </section>
        </div>

        {/* Template rules */}
        <section className="border border-gray-200 rounded bg-gray-50 p-4 text-xs text-gray-600 space-y-1">
          <p className="font-semibold text-gray-700 mb-1">Template rules:</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li><strong>Row 1</strong> is the locked header — do not modify.</li>
            <li><strong>Column A — Date:</strong> Enter in <code className="bg-gray-200 px-1">dd/mm/yyyy</code> format.</li>
            <li><strong>Column B / C — Debit / Credit ledger:</strong> Pick from the dropdown of existing ledgers.</li>
            <li><strong>Column D — Narration:</strong> Any free text.</li>
            <li><strong>Column E — Amount:</strong> Numbers only (up to 2 decimals).</li>
            <li><strong>Column F — Voucher Type:</strong> Pick Journal / Payment / Receipt / Contra (blank = Journal).</li>
            <li>You can only enter data in columns A–F. The sheet rejects anything outside.</li>
            <li>Each row becomes its own voucher with one Dr entry and one Cr entry.</li>
          </ul>
        </section>

        {/* Step 2: Preview */}
        {preview && (
          <section className="border border-gray-200 rounded bg-white overflow-hidden" data-testid="preview-section">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center gap-3">
              <div className="text-xs uppercase tracking-wider text-gray-600 font-semibold">Preview before import</div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-emerald-700 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {validRows.length} ready
                </span>
                {invalidRows.length > 0 && (
                  <span className="text-amber-700 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> {invalidRows.length} need attention (will be skipped)
                  </span>
                )}
              </div>
              <div className="ml-auto flex items-center gap-2">
                <Button variant="outline" className="h-8 text-xs" onClick={() => setPreview(null)} data-testid="btn-cancel-preview">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Discard
                </Button>
                <Button
                  className="bg-blue-700 hover:bg-blue-800 h-8 text-xs"
                  onClick={onImport}
                  disabled={busy || !validRows.length}
                  data-testid="btn-confirm-import"
                >
                  Import {validRows.length} row{validRows.length === 1 ? "" : "s"}
                </Button>
              </div>
            </div>
            <table className="tx-table">
              <thead>
                <tr>
                  <th>Row</th><th>Date</th><th>Type</th><th>Debit</th><th>Credit</th><th>Narration</th>
                  <th className="text-right">Amount</th><th>Status</th>
                </tr>
              </thead>
              <tbody data-testid="preview-rows">
                {preview.map((r) => (
                  <tr key={r.excel_row} className={r.issues.length ? "bg-amber-50/40" : ""}>
                    <td className="font-mono text-xs">{r.excel_row}</td>
                    <td className="font-mono text-xs">{r.date || <span className="text-red-600">—</span>}</td>
                    <td>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${VTYPE_BADGE[r.voucher_type] || "bg-gray-100 text-gray-700"}`}>
                        {r.voucher_type}
                      </span>
                    </td>
                    <td>{r.debit_ledger || <span className="text-red-600 italic">missing</span>}</td>
                    <td>{r.credit_ledger || <span className="text-red-600 italic">missing</span>}</td>
                    <td className="max-w-xs truncate text-gray-600">{r.narration}</td>
                    <td className="tx-num">{formatINR(r.amount)}</td>
                    <td>
                      {r.issues.length === 0 ? (
                        <span className="text-emerald-700 inline-flex items-center gap-1 text-xs">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Ready
                        </span>
                      ) : (
                        <span className="text-amber-700 inline-flex items-center gap-1 text-xs" title={r.issues.join("; ")}>
                          <AlertTriangle className="w-3.5 h-3.5" />
                          {r.issues[0]}{r.issues.length > 1 ? ` (+${r.issues.length - 1})` : ""}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Step 3: Import result */}
        {importResult && (
          <section className="border border-gray-200 rounded bg-white overflow-hidden" data-testid="result-section">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center gap-3">
              <div className="text-xs uppercase tracking-wider text-gray-600 font-semibold">Import result</div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-emerald-700 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {importResult.created} created
                </span>
                <span className="text-red-700 inline-flex items-center gap-1">
                  <XCircle className="w-3.5 h-3.5" /> {importResult.failed} failed
                </span>
              </div>
              {importResult.failed > 0 && (
                <Button variant="outline" className="ml-auto h-8 text-xs" onClick={onDownloadFailed} data-testid="btn-download-failed">
                  <Download className="w-3.5 h-3.5 mr-1" /> Download failed rows
                </Button>
              )}
            </div>
            <table className="tx-table">
              <thead>
                <tr><th>Row</th><th>Status</th><th>Voucher / Error</th></tr>
              </thead>
              <tbody data-testid="bulk-result-rows">
                {importResult.results.map((r) => (
                  <tr key={`${r.row}-${r.voucher_no || r.error}`}>
                    <td className="font-mono">{r.row}</td>
                    <td>
                      {r.ok ? (
                        <span className="text-emerald-700 inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5" /> OK
                        </span>
                      ) : (
                        <span className="text-red-700 inline-flex items-center gap-1">
                          <XCircle className="w-3.5 h-3.5" /> Error
                        </span>
                      )}
                    </td>
                    <td className="font-mono text-xs">
                      {r.ok ? r.voucher_no : <span className="text-red-700">{r.error}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </>
  );
}

export default function BulkVouchers() {
  return <RequireCompany><Inner /></RequireCompany>;
}
